import React, { useMemo, useState } from "react";
import { OverlayTrigger, Popover, Form, Button } from "react-bootstrap";
import { DateRange } from "react-date-range";
import {
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./dateRangeFilter.css";

const getPresetRange = (preset) => {
  const today = startOfDay(new Date());

  switch (preset) {
    case "today":
      return { startDate: today, endDate: today };
    case "yesterday": {
      const yesterday = subDays(today, 1);
      return { startDate: yesterday, endDate: yesterday };
    }
    case "last-week": {
      const previousWeek = subWeeks(today, 1);
      return {
        startDate: startOfWeek(previousWeek, { weekStartsOn: 0 }),
        endDate: endOfWeek(previousWeek, { weekStartsOn: 0 }),
      };
    }
    case "last-month": {
      const previousMonth = subMonths(today, 1);
      return {
        startDate: startOfMonth(previousMonth),
        endDate: endOfMonth(previousMonth),
      };
    }
    case "last-7-days":
      return { startDate: subDays(today, 6), endDate: today };
    case "last-30-days":
      return { startDate: subDays(today, 29), endDate: today };
    default:
      return { startDate: today, endDate: today };
  }
};

const PRESET_OPTIONS = [
  { value: "custom", label: "Custom" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last-week", label: "Last week" },
  { value: "last-month", label: "Last month" },
  { value: "last-7-days", label: "Last 7 days" },
  { value: "last-30-days", label: "Last 30 days" },
];

const DateRangeFilter = ({ setDate, onApplyRange, triggerId = "date-range-filter" }) => {
  const todayRange = useMemo(() => getPresetRange("today"), []);
  const [show, setShow] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("today");
  const [appliedRange, setAppliedRange] = useState({
    startDate: todayRange.startDate,
    endDate: todayRange.endDate,
    key: "selection",
  });
  const [draftRange, setDraftRange] = useState({
    startDate: todayRange.startDate,
    endDate: todayRange.endDate,
    key: "selection",
  });

  const label = `${format(appliedRange.startDate, "MM/dd/yyyy")} - ${format(
    appliedRange.endDate,
    "MM/dd/yyyy"
  )}`;

  const handlePresetChange = (event) => {
    const preset = event.target.value;
    setSelectedPreset(preset);

    if (preset === "custom") {
      return;
    }

    const nextRange = getPresetRange(preset);
    setDraftRange({ ...nextRange, key: "selection" });
  };

  const handleRangeChange = (ranges) => {
    const nextSelection = ranges.selection;
    setDraftRange({
      startDate: nextSelection.startDate,
      endDate: nextSelection.endDate || nextSelection.startDate,
      key: "selection",
    });
    setSelectedPreset("custom");
  };

  const handleApply = () => {
    const normalized = {
      startDate: draftRange.startDate,
      endDate: draftRange.endDate || draftRange.startDate,
      key: "selection",
    };

    setAppliedRange(normalized);

    if (typeof setDate === "function") {
      setDate(format(normalized.startDate, "yyyy-MM-dd"));
    }

    if (typeof onApplyRange === "function") {
      onApplyRange({
        startDate: format(normalized.startDate, "yyyy-MM-dd"),
        endDate: format(normalized.endDate, "yyyy-MM-dd"),
        preset: selectedPreset,
      });
    }

    setShow(false);
  };

  const handleCancel = () => {
    setDraftRange(appliedRange);
    setShow(false);
  };

  const popover = (
    <Popover id="date-range-popover" className="date-range-popover">
      <Popover.Body>
        <div className="date-range-filter-panel">
          <DateRange
            ranges={[draftRange]}
            onChange={handleRangeChange}
            editableDateInputs={false}
            moveRangeOnFirstSelection={false}
            showDateDisplay={false}
            months={1}
            direction="horizontal"
            maxDate={new Date()}
            rangeColors={["#ee7225"]}
          />

          <div className="date-range-filter-sidebar">
            <Form.Group>
              <Form.Label className="date-range-filter-label">Date range</Form.Label>
              <Form.Select value={selectedPreset} onChange={handlePresetChange}>
                {PRESET_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label className="date-range-filter-label">Start</Form.Label>
              <Form.Control
                value={format(draftRange.startDate, "MM/dd/yyyy")}
                readOnly
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="date-range-filter-label">End</Form.Label>
              <Form.Control
                value={format(draftRange.endDate || draftRange.startDate, "MM/dd/yyyy")}
                readOnly
              />
            </Form.Group>

            <div className="date-range-filter-actions">
              <Button className="cmn_btn" onClick={handleApply}>
                Apply
              </Button>
              <Button variant="light" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="bottom-start"
      show={show}
      onToggle={setShow}
      overlay={popover}
      rootClose
    >
      <button type="button" id={triggerId} className="date-range-filter-trigger">
        {label}
      </button>
    </OverlayTrigger>
  );
};

export default DateRangeFilter;
